/**
 * SoundGuys lab-measurement curves, fetched from the internal measurement API
 * (`/api/chart-data`) — NOT from DealHunt. Given a product's ClearBuy id, it
 * returns raw per-frequency dB series; we reshape three of them into the PDP lab
 * charts (frequency response, and isolation + noise cancelling on one chart) as SVG `d` strings.
 * The MDAQS "Audio quality" tab stays on DealHunt data.
 *
 * The PDP lab section only renders when this resolves with real data — there are no
 * placeholder curves. See docs/dealhunt-api-migration.md §A6 for the join-key contract.
 * The endpoint is an unauthenticated public read; it takes no key.
 *
 * WHICH URL is asked is not decided here: the browser goes through a same-origin proxy while
 * the measurement app sits on a host some networks block — see `chartDataProxy`.
 */
import { chartDataUrl } from './chartDataProxy'
import freqTarget from './freqResponseTarget.json'

/** Measure names in the chart-data payload that map to the three line-chart lab tabs. */
const FREQ_MEASURE = 'Smoothed Normalized Ave Raw Response'
const ISO_MEASURE = 'Isolation'
const ANC_MEASURE = 'ANC+Isolation'

// ── SVG plot geometry (log frequency axis) ───────────────────────────────────
// Shared by all three line charts. The curves are built here and the gridlines/axis labels in
// LabData.tsx, so both sides read these numbers rather than repeating the literals — a viewBox
// change that only landed on one side would silently slide the grid off the data.
//
// Two profiles, and the split is deliberately VERTICAL ONLY: `padL`, `plotW` and `viewW` are
// identical across them. That is what lets `freqX` / `freqFromX`, the gridline x-positions and
// the pointer hit-test stay single, profile-free module state — only the dB→y mapping, the frame
// height and the label size vary, and all of those live behind `chartGeometry` below.

/**
 * The horizontal frame, owned by ONE object rather than copied into each profile. `freqX`,
 * `freqFromX`, the gridline x-positions, the axis-label x's and the pointer hit-test all read
 * it directly and take no profile — which is only sound while every profile shares it, so the
 * profiles spread it in rather than restating the numbers. Widening one profile's gutter is
 * then a deliberate edit here (and a signal that the x-side needs a profile too), not a typo
 * that silently puts the crosshair a few hertz off the curve it is pinning.
 */
export const PLOT_X = {
  /** Left gutter holding the dB tick labels and the rotated axis title. */
  padL: 58,
  /** Plot width, 20 Hz → 20 kHz. */
  plotW: 528,
  viewW: 600,
} as const

export interface PlotProfile {
  padL: number
  plotW: number
  padT: number
  plotH: number
  viewW: number
  viewH: number
  /** Axis-label size in viewBox user units — the SVG scales to its container, so this is
   *  relative to `viewW`, not a CSS pixel size. */
  labelSize: number
  /** Hover-pin radius, same units. */
  pinR: number
}

/** Desktop/tablet frame: wide and shallow, sized for the 700–900px-wide lab card at bp(md)+. */
export const PLOT: PlotProfile = {
  ...PLOT_X,
  padT: 14,
  plotH: 208,
  viewH: 268,
  labelSize: 10,
  pinR: 4,
}

/**
 * Phone frame: identical horizontally, far taller and with bigger labels. The SVG's own units
 * are what shrink when it scales down, so at ~340px of rendered width the desktop profile draws
 * a 133px-tall sliver of curve under ~6px axis labels. Growing the frame — rather than the
 * container — is the only thing that buys back vertical detail.
 *
 * `viewW / viewH` here is also declared as `aspect-ratio` on `.chart` in LabData.module.css:
 * the profile is only known after hydration, so CSS has to pin the box height per breakpoint
 * ahead of it. The two must agree — `audio-chart CSS aspect-ratio` in the spec asserts it.
 */
export const PLOT_COMPACT: PlotProfile = {
  ...PLOT,
  plotH: 338,
  viewH: 398,
  labelSize: 15,
  pinR: 6,
}

const LOG_F0 = Math.log10(20)
const LOG_F1 = Math.log10(20000)

export type Point = [freq: number, db: number]

/** Frequency (Hz, log scale) → x coordinate. Exported so axis labels sit on the same scale. */
export function freqX(f: number): number {
  return PLOT_X.padL + ((Math.log10(f) - LOG_F0) / (LOG_F1 - LOG_F0)) * PLOT_X.plotW
}

/** Inverse of `freqX`: the frequency under a viewBox x-coordinate (for hover hit-testing). */
export function freqFromX(x: number): number {
  return 10 ** (LOG_F0 + ((x - PLOT_X.padL) / PLOT_X.plotW) * (LOG_F1 - LOG_F0))
}

/**
 * Nearest measured sample to `hz` by log-frequency distance. The hover readout states only
 * values the lab actually measured — same rule as the data tables: never interpolate.
 */
export function nearestPoint(points: Point[], hz: number): Point {
  const target = Math.log10(hz)
  let best = points[0]
  let bestDist = Infinity
  for (const p of points) {
    const d = Math.abs(Math.log10(p[0]) - target)
    if (d < bestDist) {
      bestDist = d
      best = p
    }
  }
  return best
}

// ── Lab palette, measures and tabs ───────────────────────────────────────────
// Every lab surface (PDP section, category dialog and drawer, sparklines, card rows) reads these,
// so a colour or a tab key cannot be restated on one surface and drift from the others.

/** The primary curve ("main trace"): cyan on the PDP, "this card" on the category page. */
export const LAB_PRIMARY_COLOR = '#29F5EC'

/**
 * Overlay colours for comparison curves, in assignment order. The red is the declared pair's
 * (`YOURS_COLOR`, `lib/owner-notes/curves`): red = yours on every surface, so a PDP deep link
 * from a category card draws the pair in the colour the card used.
 *
 * The palette (and `LAB_TARGET_COLOR`) matches the internal measurement tool and only clears
 * contrast on its dark surface, which is why the chart frame stays dark in both themes. The red
 * is the tool's #BA1B0C lightened to #C82314: 3.04:1 on the frame (#1b1b1c; WCAG 1.4.11), was 2.66.
 */
export const LAB_PEER_COLORS: readonly string[] = ['#FFF700', '#C82314', '#2C92F3']

/** The SoundGuys house target curve, drawn dashed on the frequency chart. */
export const LAB_TARGET_COLOR = '#F35FAA'

/** The three line-chart measures (`LabChartPaths` keys). */
export type LabMeasure = 'frequency' | 'isolation' | 'anc'

/** A lab tab: frequency response, isolation and ANC on one chart, then MDAQS "Audio quality". */
export type LabTabKey = 'freq' | 'atten' | 'mdaqs'

/**
 * The one measure a line-chart tab stands for, where a caller needs a single curve (band chips,
 * the pair fill, the hover difference). The attenuation tab draws both isolation and ANC; ANC
 * wins when the primary product has it, since that is the number a reader shops on.
 */
export function tabMeasure(tab: Exclude<LabTabKey, 'mdaqs'>, curves: LabChartPaths): LabMeasure {
  if (tab === 'freq') {
    return 'frequency'
  }
  return curves.anc ? 'anc' : 'isolation'
}

/** A frequency for people: "250 Hz", "4 kHz", "12.5 kHz". */
export function hzText(hz: number): string {
  if (hz < 1000) {
    return `${Math.round(hz)} Hz`
  }
  return `${Number((hz / 1000).toFixed(1))} kHz`
}

/**
 * Everything that depends on the plot's VERTICAL geometry, bound to one profile. The dB→y
 * mapping, the paths built through it and the y-axis ticks all have to agree on the same frame
 * height, so they are handed out together rather than each taking a profile argument — a call
 * site that passed the desktop frame to one and the phone frame to another would draw a curve
 * that misses its own gridlines.
 */
export interface ChartGeometry {
  plot: PlotProfile
  /** `viewBox` the line charts render at — the frame these coordinates are expressed in. */
  viewBox: string
  /** Pixel y for a dB value, clamped to the plot box exactly like the curves themselves — so a
   *  hover pin on a capped-off sample sits on the frame edge, not outside it. */
  scaleY: (scale: AxisScale, db: number) => number
  /** SVG `d` for a curve (line only) scaled into the given range. */
  scalePath: (points: Point[], scale: AxisScale) => string
  /**
   * Y-axis gridline ticks (label + pixel y) for a resolved scale. `signed` marks positive values
   * with a leading `+`, which reads as "above the target" on the frequency chart; attenuation is
   * a magnitude, so those ticks are plain numbers.
   */
  axisTicks: (scale: AxisScale, signed?: boolean) => AxisTick[]
}

/** `b` resampled onto `a`'s frequencies (linear in log-f), inside the range both cover.
 *  Both inputs ascend in frequency, as every curve the charts draw does. */
function alignCurves(a: Point[], b: Point[]): Array<[hz: number, a: number, b: number]> {
  const out: Array<[number, number, number]> = []
  if (b.length < 2) {
    return out
  }
  let j = 0
  for (const [hz, db] of a) {
    if (hz < b[0][0] || hz > b[b.length - 1][0]) {
      continue
    }
    while (j < b.length - 2 && b[j + 1][0] < hz) {
      j++
    }
    const [h0, d0] = b[j]
    const [h1, d1] = b[j + 1]
    const t = h1 === h0 ? 0 : (Math.log10(hz) - Math.log10(h0)) / (Math.log10(h1) - Math.log10(h0))
    out.push([hz, db, d0 + (d1 - d0) * t])
  }
  return out
}

export function chartGeometry(plot: PlotProfile): ChartGeometry {
  const yTop = plot.padT
  const yBottom = plot.padT + plot.plotH

  /**
   * Clamp a y-coordinate to the plot box. The axes are fixed (`FREQ_SCALE` / `ATTEN_SCALE`), so a
   * sample past them — a deep treble roll-off, bass leakage below 0 dB of isolation — rides the
   * frame edge instead of escaping it. Robust where an SVG `clip-path` is not.
   */
  const clampY = (y: number): number => Math.min(yBottom, Math.max(yTop, y))

  /** dB → y, mapping `[dbMin, dbMax]` onto the plot box (dbMax at top). */
  const dbYFor =
    (dbMin: number, dbMax: number) =>
    (db: number): number => {
      const span = dbMax - dbMin || 1
      return plot.padT + ((dbMax - db) / span) * plot.plotH
    }

  const linePath = (points: Point[], yFor: (db: number) => number): string =>
    points
      .map((p, i) => `${i ? 'L' : 'M'}${freqX(p[0]).toFixed(1)} ${clampY(yFor(p[1])).toFixed(1)}`)
      .join(' ')

  return {
    plot,
    viewBox: `0 0 ${plot.viewW} ${plot.viewH}`,
    scaleY: (scale, db) => clampY(dbYFor(scale.min, scale.max)(db)),
    scalePath: (points, { min, max }) => linePath(points, dbYFor(min, max)),
    axisTicks: ({ min, max, step }, signed = true) => {
      const yFor = dbYFor(min, max)
      const ticks: AxisTick[] = []
      for (let db = min; db <= max + 1e-6; db += step) {
        const v = Math.round(db)
        ticks.push({ label: signed && v > 0 ? `+${v}` : `${v}`, y: yFor(db) })
      }
      return ticks
    },
  }
}

/** The two frames LabData switches between (see `PLOT_COMPACT`). Module-level, so picking one
 *  per render is a reference swap, not a rebuild. */
export const GEOM = chartGeometry(PLOT)
export const GEOM_COMPACT = chartGeometry(PLOT_COMPACT)

// ── Chart-data payload ───────────────────────────────────────────────────────

/** One measurement series: `hz[i]` ↔ `response[i]` (dB). */
interface RpcMeasure {
  hz: number[]
  response: number[]
  time: string
}

/** The measurement app's `/api/chart-data` payload (the shape the old ClearBuy
 *  `get_chart_data` RPC returned; the `Rpc*` type names are that history): fixed metadata
 *  keys plus per-measure objects. */
interface RpcResponse {
  [measure: string]: RpcMeasure | string | number | null
  prod_id: number
  prod_name: string
  measure_name: string
}

/**
 * Chart data for the line-chart lab tabs. Each measure is `null` when the product's payload
 * doesn't carry it, so LabData renders only the tabs that have data.
 *
 * All three carry raw `points` (isolation/ANC in positive dB of attenuation; frequency
 * nudged onto the target, see `minDistNudge`) rather than paths: the charts draw them at two
 * frame profiles, and the sparklines and band chips read the numbers.
 */
export interface LabChartPaths {
  frequency: { points: Point[]; blurb: string } | null
  isolation: { points: Point[]; blurb: string } | null
  anc: { points: Point[]; blurb: string } | null
}

/** The visible blurb links this phrase to the house-curve explainer; the SVG `<desc>` gets the
 *  concatenated plain string. Exported in parts so the linked and plain renderings can't drift. */
export const FREQ_CURVE_LINK_HREF = 'https://www.soundguys.com/soundguys-house-curve-54290/'
export const FREQ_BLURB_PARTS = {
  before: 'Measured frequency response (dB relative) against the ',
  linkText: 'SoundGuys Headphone Preference Curve',
  after:
    '. When the measurement goes above the dashed line, that frequency is louder than our ' +
    "preference curve, and when it goes below: it's quieter. Use the curve for comparison " +
    'purposes only, as your taste may vary.',
}
const FREQ_BLURB = FREQ_BLURB_PARTS.before + FREQ_BLURB_PARTS.linkText + FREQ_BLURB_PARTS.after
// Isolation and ANC share one chart, so the panel joins whichever of these are present and
// closes with `ATTEN_BLURB_TAIL`.
const ISO_BLURB =
  'Solid lines: passive isolation (dB) with ANC off — how much ambient noise the fit ' +
  'blocks on its own.'
const ANC_BLURB =
  'Dashed lines: total attenuation (dB) with ANC on — passive isolation plus active ' +
  'noise cancelling.'
export const ATTEN_BLURB_TAIL = 'Higher on the chart means more attenuation.'

/** Pull a measure object out of the payload, or undefined when absent/null. */
function measure(rpc: RpcResponse, name: string): RpcMeasure | undefined {
  const v = rpc[name]
  return v !== null && typeof v === 'object' ? v : undefined
}

/**
 * Zip `hz`/`response` into points, drop non-finite samples, and sort by frequency
 * ascending — the chart-data payload has some measures descending (20k→20), others ascending.
 */
function toPoints(m: RpcMeasure | undefined): Point[] | null {
  if (!m || !Array.isArray(m.hz) || !Array.isArray(m.response)) {
    return null
  }
  const pts = m.hz
    .map((hz, i): Point => [hz, m.response[i]])
    .filter(([hz, db]) => Number.isFinite(hz) && hz > 0 && Number.isFinite(db))
    .sort((a, b) => a[0] - b[0])
  return pts.length ? pts : null
}

/**
 * A series that is exactly 0 dB at every sample is a placeholder, not a measurement: the API
 * ships one as `ANC+Isolation` for products without ANC (AirPods 4, id 6092, 120 zero samples),
 * which would otherwise draw a flat line, add a "Noise cancelling" tab, and give the category
 * card's "versus yours" ANC comparison a 0 dB baseline to subtract from. Dropped here so every
 * consumer sees "no measurement". A real curve is never identically zero.
 */
function measured(points: Point[] | null): Point[] | null {
  return points && points.some(([, db]) => db !== 0) ? points : null
}

/**
 * The SoundGuys house preference/target curve — the "ideal" response a headphone is graded
 * against. It's the same for every product and changes rarely, so it ships as a static JSON
 * snapshot (`freqResponseTarget.json`, sourced from /api/targets/default) rather than a fetch.
 * Shares the `hz`/`response` shape of a chart-data measure, so it reuses the same point helper.
 * Exported as raw points (not a path): it is drawn at either frame profile and is the yardstick
 * `minDistNudge` aligns every frequency curve to.
 */
export const FREQ_TARGET_POINTS = toPoints(freqTarget.curve as unknown as RpcMeasure)
export const FREQ_TARGET_NAME = freqTarget.display_name

// ── Fixed axes ────────────────────────────────────────────────────────────────
// Every chart, product and overlay set shares one dB frame per measure, like the internal
// measurement tool's charts — two products read against each other at a glance, and the frame
// never jumps when an overlay is added. Samples past it ride the edge via `clampY`.

/** Frequency response: ±20 dB around the target, the internal tool's frame. */
export const FREQ_SCALE: AxisScale = { min: -20, max: 20, step: 5 }

/** Isolation and ANC: 0 dB ("blocks nothing") is always the floor. The internal tool tops out at
 *  50, but flagship ANC peaks past it (WH-1000XM6, 6–9 kHz) and would plateau on the frame. */
export const ATTEN_SCALE: AxisScale = { min: 0, max: 60, step: 10 }

/**
 * Shift a frequency-response measurement vertically onto the target: the mean of
 * (target − measurement) over 100 Hz–6 kHz is added to every sample. Port of the internal
 * measurement tool's normalization, so a curve sits where the tool draws it — the raw series'
 * absolute level is arbitrary, only its shape against the target means anything.
 */
export function minDistNudge(target: Point[], measurement: Point[]): Point[] {
  const tgt = target
    .filter(([hz, db]) => hz > 0 && Number.isFinite(hz) && Number.isFinite(db))
    .sort((a, b) => a[0] - b[0])
  const band = measurement
    .filter(([hz, db]) => hz >= 100 && hz <= 6000 && Number.isFinite(db))
    .sort((x, y) => x[0] - y[0])
  const pairs = alignCurves(band, tgt)
  const nudge = pairs.length ? pairs.reduce((sum, [, m, t]) => sum + t - m, 0) / pairs.length : 0
  return measurement.map(([hz, db]): Point => [hz, db + nudge])
}

/**
 * Decimate a measurement series to a compact log-spaced sample for the data table each
 * chart renders alongside its SVG (SEO audit C2: the curves reached crawlers as `path d=`
 * pixels only — no numeric series anywhere in the page). ~31 rows is close to
 * third-octave resolution: enough to reconstruct the curve's shape, small enough not to
 * bloat the HTML. Picks the nearest REAL sample to each log-spaced target frequency —
 * never interpolates, so the table only ever states values the lab actually measured.
 */
export function tablePoints(points: Point[], maxRows = 31): Point[] {
  if (points.length <= maxRows) {
    return points
  }
  const logLo = Math.log10(points[0][0])
  const logHi = Math.log10(points[points.length - 1][0])
  const out: Point[] = []
  let idx = 0
  for (let i = 0; i < maxRows; i++) {
    const target = 10 ** (logLo + ((logHi - logLo) * i) / (maxRows - 1))
    while (idx < points.length - 1 && points[idx][0] < target) {
      idx++
    }
    const prev = points[Math.max(0, idx - 1)]
    const pick =
      Math.abs(prev[0] - target) <= Math.abs(points[idx][0] - target) ? prev : points[idx]
    if (!out.length || out[out.length - 1][0] !== pick[0]) {
      out.push(pick)
    }
  }
  return out
}

/** One y-axis gridline: its label and pixel position. */
export interface AxisTick {
  label: string
  y: number
}

/** A dB axis: bounds plus the gridline step. */
export interface AxisScale {
  min: number
  max: number
  step: number
}

/**
 * Build the line-chart paths from a chart-data payload, one entry per measure that's present.
 * Returns `null` when the payload carries none of the three — the caller then hides the
 * whole lab section (no placeholder curves).
 */
export function buildLabCharts(rpc: RpcResponse): LabChartPaths | null {
  const rawFreq = measured(toPoints(measure(rpc, FREQ_MEASURE)))
  const freq = rawFreq && FREQ_TARGET_POINTS ? minDistNudge(FREQ_TARGET_POINTS, rawFreq) : rawFreq
  const iso = measured(toPoints(measure(rpc, ISO_MEASURE)))
  const anc = measured(toPoints(measure(rpc, ANC_MEASURE)))
  if (!freq && !iso && !anc) {
    return null
  }
  return {
    frequency: freq ? { points: freq, blurb: FREQ_BLURB } : null,
    isolation: iso ? { points: iso, blurb: ISO_BLURB } : null,
    anc: anc ? { points: anc, blurb: ANC_BLURB } : null,
  }
}

/**
 * A curve request that got no answer — network error, timeout, or the endpoint failing —
 * as opposed to the lab answering "nothing measured for this id" (`null`). The two used to be
 * one `null`, and a reader whose network blocked the endpoint saw every pick labelled
 * "no data" (2026-09-19).
 */
export class ChartRequestError extends Error {
  constructor(clearbuyId: number, cause: unknown) {
    super(`lab chart request failed for ${clearbuyId}: ${String(cause)}`)
    this.name = 'ChartRequestError'
  }
}

/**
 * One request, three outcomes: the payload; `null` when the lab has nothing for the id — which
 * surfaces as both `404` and `200` with an unusable body depending on the id, so neither status
 * nor body alone is sufficient and we check both; or a thrown `ChartRequestError` when there
 * was no answer at all. `AbortError` (caller-cancelled) passes through untouched.
 */
async function requestAudioChart(
  clearbuyId: number,
  signal?: AbortSignal
): Promise<RpcResponse | null> {
  let res: Response
  let json: RpcResponse | null
  try {
    res = await fetch(chartDataUrl(clearbuyId), { signal, headers: { Accept: 'application/json' } })
    if (res.status === 404) {
      return null
    }
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`)
    }
    json = (await res.json()) as RpcResponse | null
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') {
      throw error
    }
    throw new ChartRequestError(clearbuyId, error)
  }
  if (!json || typeof json !== 'object' || typeof json.prod_id !== 'number') {
    return null
  }
  return json
}

/**
 * Fetch a product's lab curves by ClearBuy id. Returns `null` on any failure or when the id
 * has no data; throws only on `AbortError`. For callers that gate a section on "is there a
 * chart" and treat a failure the same way (SSR, the PDP's peer prefetch). A caller that must
 * tell the two apart uses `loadLabChartsOrThrow`.
 */
export async function fetchAudioChart(
  clearbuyId: number,
  signal?: AbortSignal
): Promise<RpcResponse | null> {
  try {
    return await requestAudioChart(clearbuyId, signal)
  } catch (error) {
    if (error instanceof ChartRequestError) {
      console.error('[audio-chart] request failed:', clearbuyId, error)
      return null
    }
    throw error
  }
}

/**
 * Convenience for SSR: resolve a ClearBuy id straight to chart paths (or `null`).
 * Folds the fetch + transform so `getServerSideProps` can gate the lab section on one call.
 */
export async function loadLabCharts(
  clearbuyId: number | null | undefined,
  signal?: AbortSignal
): Promise<LabChartPaths | null> {
  if (clearbuyId === null || clearbuyId === undefined) {
    return null
  }
  const rpc = await fetchAudioChart(clearbuyId, signal)
  return rpc ? buildLabCharts(rpc) : null
}

/**
 * `loadLabCharts` for a caller that shows a failure differently from "nothing measured":
 * chart paths, `null` when the lab has none, and a thrown `ChartRequestError` when the request
 * got no answer.
 */
export async function loadLabChartsOrThrow(clearbuyId: number): Promise<LabChartPaths | null> {
  const rpc = await requestAudioChart(clearbuyId)
  return rpc ? buildLabCharts(rpc) : null
}
